import { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Reorder, useDragControls, AnimatePresence, motion } from "motion/react";

import useWizard from "../useWizard";
import { WizardInput } from "../parts";
import { ACCENT_COLOR } from "../theme";

function ProgramInfo() {
  const { data, updateField, nextSubStep, isCurrentStepValid } = useWizard();
  const info = data.generalInfo;

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (isCurrentStepValid) nextSubStep();
    }
  };

  return (
    <Content>
      <div>
        <Title className="merri">Program Info</Title>
        <Subtitle className="raleway">
          Tell us about your Religious Education program
        </Subtitle>
      </div>

      <FormCenter>
        <WizardInput
          label="Program Name"
          icon="far fa-clipboard"
          value={info.programName}
          onChange={(v) => updateField("generalInfo", "programName", v)}
          onKeyDown={handleKeyDown}
          placeholder="St. Mary's Religious Education 2026"
          autoFocus
          multiline
        />
      </FormCenter>
    </Content>
  );
}

function ClassItemRow({ item, index, total, showCapacity, onUpdateClass, onUpdateCapacity, onRemove, onDragEnd, autoFocus, classInputRef, capacityInputRef, rowIndex, onClassKeyDown, onCapacityKeyDown, layoutTransition }) {
  const classRef = useRef(null);
  const capRef = useRef(null);
  const [showWarning, setShowWarning] = useState(false);
  const classBlurTimer = useRef(null);

  const checkWarning = () => {
    const name = classRef.current?.value ?? "";
    const cap = capRef.current?.value ?? "";
    setShowWarning(name.trim() && !cap.trim());
  };

  const handleClassBlur = () => {
    classBlurTimer.current = setTimeout(() => checkWarning(), 0);
  };

  const handleCapFocus = () => {
    clearTimeout(classBlurTimer.current);
  };

  useEffect(() => {
    classInputRef.current[rowIndex] = classRef.current;
    capacityInputRef.current[rowIndex] = capRef.current;
  });
  useEffect(() => {
    if (showCapacity) checkWarning();
    else setShowWarning(false);
  }, [showCapacity]); // eslint-disable-line react-hooks/exhaustive-deps
  const dragControls = useDragControls();
  const [dragging, setDragging] = useState(false);

  return (
    <Reorder.Item
      value={item}
      dragListener={false}
      dragControls={dragControls}
      layout="position"
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ layout: layoutTransition, height: { duration: 0.25, ease: [0.4, 0, 0.2, 1] }, opacity: { duration: 0.2 } }}
      style={{ listStyle: "none", overflow: "hidden" }}
      onDragStart={() => setDragging(true)}
      onDragEnd={(e) => { setDragging(false); onDragEnd(e); }}
    >
      <ClassRow $alt={item.displayIndex % 2 === 0} $last={index === total - 1} $dragging={dragging}>
        <ClassInputs>
          <ClassNameWrap>
            <WizardInput
              label={`Class ${item.displayIndex + 1}`}
              icon="fa-chalkboard"
              value={item.name}
              onChange={(v) => { onUpdateClass(v); if (!v.trim()) setShowWarning(false); }}
              placeholder="Class Name"
              autoFocus={autoFocus}
              inputRef={classRef}
              onKeyDown={onClassKeyDown}
              onBlur={handleClassBlur}
            />
          </ClassNameWrap>
          <AnimatePresence initial={false}>
            {showCapacity && (
              <motion.div
                key="capacity"
                initial={{ width: 0, marginLeft: 0, clipPath: "inset(0px 0px 0px 0px)" }}
                animate={{ width: 120, marginLeft: 20, clipPath: "inset(-7px -7px -7px -7px)" }}
                exit={{ width: 0, marginLeft: 0, clipPath: "inset(0px 0px 0px 0px)" }}
                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                style={{ flexShrink: 0 }}
              >
                <CapacityInner
                  $warning={showWarning}
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.85 }}
                  transition={{ duration: 0.2, delay: 0.1 }}
                >
                  <WizardInput
                    label="Capacity"
                    icon="fa-users"
                    value={item.capacity}
                    onChange={(v) => { onUpdateCapacity(v); if (v.trim()) setShowWarning(false); }}
                    onFocus={handleCapFocus}
                    placeholder="0"
                    type="number"
                    inputRef={capRef}
                    onKeyDown={onCapacityKeyDown}
                    onBlur={checkWarning}
                  />
                  <AnimatePresence>
                    {showWarning && (
                      <motion.div
                        key="cap-warn"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: "hidden" }}
                      >
                        <CapacityWarning>
                          <i className="fas fa-triangle-exclamation" />
                          Missing capacity
                        </CapacityWarning>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </CapacityInner>
              </motion.div>
            )}
          </AnimatePresence>
        </ClassInputs>
        {total > 1 && (
          <HandleArea>
            <DragHandle onPointerDown={(e) => dragControls.start(e)}>
              <i className="fas fa-grip-vertical" />
            </DragHandle>
            <DeleteButton onClick={onRemove} tabIndex={-1}>
              <i className="fas fa-trash-can" />
            </DeleteButton>
          </HandleArea>
        )}
      </ClassRow>
    </Reorder.Item>
  );
}

function Classes() {
  const { data, updateField } = useWizard();
  const classes = data.generalInfo.classes || ["", "", ""];
  const showCapacity = data.generalInfo.showCapacity || false;
  const capacities = data.generalInfo.capacities || classes.map(() => "");
  const [nextId, setNextId] = useState(() => classes.length);
  const [, forceUpdate] = useState(0);
  const layoutTransition = useRef({ duration: 0 });
  useEffect(() => {
    const t = setTimeout(() => { layoutTransition.current = { type: "spring", stiffness: 400, damping: 30 }; }, 300);
    return () => clearTimeout(t);
  }, []);
  const itemsRef = useRef(
    classes.map((name, i) => ({ id: i, name, capacity: capacities[i] ?? "20", displayIndex: i }))
  );

  // Sync items ref with current wizard state
  // Rebuild only when length changes (add/delete), otherwise update in place
  if (itemsRef.current.length !== classes.length) {
    // Length changed — could be add or delete, rebuild from scratch with current IDs
    // This is handled by addClass/removeClass which update itemsRef directly
  }
  // Always sync values from wizard state
  itemsRef.current.forEach((item, i) => {
    if (i < classes.length) {
      item.name = classes[i];
      item.capacity = capacities[i] ?? "20";
    }
  });

  const items = itemsRef.current;
  const initialFocusIndex = useRef(classes.findIndex((c) => !c.trim()));
  const classRefs = useRef({});
  const capacityRefs = useRef({});
  const pendingFocus = useRef(null);

  useEffect(() => {
    if (pendingFocus.current !== null) {
      const idx = pendingFocus.current;
      pendingFocus.current = null;
      setTimeout(() => classRefs.current[idx]?.focus(), 280);
    }
  });

  const handleClassKeyDown = (e, index) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (showCapacity) {
        capacityRefs.current[index]?.focus();
      } else if (index + 1 < classes.length) {
        classRefs.current[index + 1]?.focus();
      } else {
        addClass();
      }
    } else if (e.key === "Tab" && !e.shiftKey && !showCapacity && index + 1 >= classes.length) {
      e.preventDefault();
      addClass();
    }
  };

  const handleCapacityKeyDown = (e, index) => {
    const isLast = index + 1 >= classes.length;
    if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey && isLast)) {
      e.preventDefault();
      if (index + 1 < classes.length) {
        classRefs.current[index + 1]?.focus();
      } else {
        addClass();
      }
    }
  };

  const handleReorder = (newItems) => {
    itemsRef.current = newItems;
    updateField("generalInfo", "classes", newItems.map((it) => it.name));
    updateField("generalInfo", "capacities", newItems.map((it) => it.capacity));
  };

  const handleDragEnd = () => {
    itemsRef.current.forEach((item, i) => { item.displayIndex = i; });
    forceUpdate(n => n + 1);
  };

  const updateClass = (index, value) => {
    const next = [...classes];
    next[index] = value;
    updateField("generalInfo", "classes", next);
  };

  const updateCapacity = (index, value) => {
    const next = [...capacities];
    next[index] = value;
    updateField("generalInfo", "capacities", next);
  };

  const addClass = () => {
    const lastCapacity = capacities[capacities.length - 1] || "20";
    const newItem = { id: nextId, name: "", capacity: lastCapacity, displayIndex: itemsRef.current.length };
    itemsRef.current = [...itemsRef.current, newItem];
    pendingFocus.current = classes.length;
    setNextId(nextId + 1);
    updateField("generalInfo", "classes", [...classes, ""]);
    updateField("generalInfo", "capacities", [...capacities, lastCapacity]);
  };

  const removeClass = (index) => {
    if (classes.length <= 1) return;
    itemsRef.current = itemsRef.current.filter((_, i) => i !== index);
    itemsRef.current.forEach((item, i) => { item.displayIndex = i; });
    updateField("generalInfo", "classes", classes.filter((_, i) => i !== index));
    updateField("generalInfo", "capacities", capacities.filter((_, i) => i !== index));
  };

  return (
    <ClassesLayout>
      <ClassesScrollArea>
        <div>
          <Title className="merri">Classes</Title>
          <Subtitle className="raleway">
            Let's get your classes set up. We just need the class names - these can be grades, etc. We'll get more details later if we need them.
          </Subtitle>
        </div>

        <Reorder.Group
          axis="y"
          values={items}
          onReorder={handleReorder}
          as="div"
          style={{ display: "flex", flexDirection: "column", margin: "0 -40px", padding: 0 }}
        >
          <AnimatePresence initial={false}>
            {items.map((item, i) => (
              <ClassItemRow
                key={item.id}
                item={item}
                index={i}
                total={items.length}
                showCapacity={showCapacity}
                onUpdateClass={(v) => updateClass(i, v)}
                onUpdateCapacity={(v) => updateCapacity(i, v)}
                onRemove={() => removeClass(i)}
                onDragEnd={handleDragEnd}
                autoFocus={i === initialFocusIndex.current}
                classInputRef={classRefs}
                capacityInputRef={capacityRefs}
                rowIndex={i}
                onClassKeyDown={(e) => handleClassKeyDown(e, i)}
                onCapacityKeyDown={(e) => handleCapacityKeyDown(e, i)}
                layoutTransition={layoutTransition.current}
              />
            ))}
          </AnimatePresence>
        </Reorder.Group>

        <AddButtonWrap>
          <AddButton onClick={addClass}>
            <i className="fas fa-plus" /> Add Another Class
          </AddButton>
        </AddButtonWrap>

      </ClassesScrollArea>

      <OptionsBar>
        <CheckboxLabel>
          <Checkbox
            type="checkbox"
            checked={showCapacity}
            onChange={() => updateField("generalInfo", "showCapacity", !showCapacity)}
          />
          My classes have a maximum capacity
          <HelpLink href="https://help.flocknote.com" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
            <i className="far fa-circle-question" />
          </HelpLink>
        </CheckboxLabel>
      </OptionsBar>
    </ClassesLayout>
  );
}

export default function GeneralInfo({ subStep }) {
  return subStep === 0 ? <ProgramInfo /> : <Classes />;
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 24px;
`;

const FormCenter = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 24px;
`;

const ClassesLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100% + 80px);
  margin: -40px;
`;

const ClassesScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 40px 40px 12px;
`;

const Title = styled.h2`
  font-size: 32px;
  margin: 0;
  color: #182941;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #6b7280;
  margin: 12px 0 0 0;
`;

const HandleArea = styled.div`
  position: absolute;
  right: 4px;
  top: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: 2;
`;

const DragHandle = styled.div`
  color: #d1d5db;
  font-size: 16px;
  cursor: grab;
  padding: 4px 8px;
  transition: color 0.2s;

  &:hover {
    color: #9ca3af;
  }

  &:active {
    cursor: grabbing;
  }
`;

const DeleteButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  color: #d1d5db;
  font-size: 16px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: color 0.2s, transform 0.15s;

  &:hover {
    color: #ef4444;
  }

  &:active {
    transform: scale(0.8);
  }
`;

const ClassInputs = styled.div`
  display: flex;
  flex: 1;
`;

const ClassNameWrap = styled.div`
  flex: 1;
`;


const ClassRow = styled.div`
  position: relative;
  background: ${(p) => (p.$alt ? "#fff" : "#f5f5f5")};
  padding: 12px 40px 19px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  border-bottom: ${(p) => (p.$last || p.$dragging ? "1px solid rgba(0, 0, 0, 0.06)" : "none")};
  transition: background 0.3s;
  user-select: none;

  &:hover ${HandleArea},
  &:focus-within ${HandleArea} {
    opacity: 1;
  }
`;

const AddButtonWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: -12px;
`;

const AddButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  color: ${ACCENT_COLOR};
  font-size: 17px;
  font-family: inherit;
  cursor: pointer;
  padding: 10px 20px;
  border-radius: 100px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #e5e7eb;
  }
`;

const CapacityInner = styled(motion.div)`
  input {
    border-color: ${(p) => (p.$warning ? "#f59e0b" : "#d1d5db")};
    ${(p) => p.$warning && "border-bottom-left-radius: 0; border-bottom-right-radius: 0;"}

    &:focus {
      ${(p) => p.$warning && "box-shadow: 0 0 4px 2px #f59e0b;"}
    }
  }
`;

const CapacityWarning = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 3px 0 4px;
  background: #fef9e7;
  border: 1px solid #f59e0b;
  border-top: none;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  color: #b45309;
  font-size: 11px;
  overflow: hidden;
  white-space: nowrap;

  i {
    color: #f59e0b;
    font-size: 11px;
    flex-shrink: 0;
  }
`;

const OptionsBar = styled.div`
  padding: 8px 40px;
  border-top: 1px solid #e5e7eb;
  background: #ededef;
  flex-shrink: 0;
`;

const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #4b5563;
  cursor: pointer;
`;

const Checkbox = styled.input`
  width: 15px;
  height: 15px;
  accent-color: ${ACCENT_COLOR};
  cursor: pointer;
`;

const HelpLink = styled.a`
  color: #9ca3af;
  font-size: 14px;
  margin-left: -4px;
  transition: color 0.15s, transform 0.15s;

  &:hover {
    color: ${ACCENT_COLOR};
  }

  &:active {
    transform: scale(0.85);
  }
`;

